import { useState } from 'react';
import { Button } from './ui/button';
import { LogOut } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

/**
 * "End my day" — single-click clean shutdown:
 *  1. If currently punched in → punch out
 *  2. If currently on lunch → end lunch
 *  3. If currently on network downtime → end it
 *  4. Stop any running task-timer (best-effort, ignored on error)
 *  5. Clear local auth token and redirect to /login
 *
 * Confirms with the user first so an accidental click doesn't log them out.
 */
export const EndMyDayButton = () => {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (busy) return;
    const ok = window.confirm(
      "End your day?\n\nThis will:\n• Punch you out (if punched in)\n• End any active lunch / network downtime\n• Stop any running task timers\n• Log you out"
    );
    if (!ok) return;
    setBusy(true);
    try {
      // Run cleanups in parallel; ignore individual failures so logout still proceeds.
      const safe = async (fn) => { try { await fn(); } catch (e) { /* ignore */ } };
      const [att, lunch, net] = await Promise.all([
        api.get('/attendance/me/today').catch(() => ({ data: { is_punched_in: false } })),
        api.get('/lunch-break/active').catch(() => ({ data: null })),
        api.get('/network-downtime/active').catch(() => ({ data: null })),
      ]);
      const promises = [];
      if (att.data?.is_punched_in) promises.push(safe(() => api.post('/attendance/toggle')));
      if (lunch.data)              promises.push(safe(() => api.post('/lunch-break/toggle')));
      if (net.data)                promises.push(safe(() => api.post('/network-downtime/toggle')));
      await Promise.all(promises);

      // Note: any running task-timer is left running — the server-side
      // attendance auto-close + stale-timer cleanup handle the edge case at
      // 11:59 PM. Forcing a stop here can leak time-logs if the user just
      // mis-clicked.

      toast.success('Goodbye! See you tomorrow 👋');
      // Clear local token and redirect
      try { localStorage.removeItem('token'); } catch { /* ignore */ }
      window.location.href = '/login';
    } catch (e) {
      toast.error('Could not fully end the day — logging out anyway');
      try { localStorage.removeItem('token'); } catch { /* ignore */ }
      window.location.href = '/login';
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      size="default"
      onClick={handleClick}
      disabled={busy}
      className="bg-slate-700 hover:bg-slate-800 text-white shadow-lg hover:shadow-xl ring-2 ring-slate-300 gap-1.5 rounded-full px-4"
      data-testid="end-my-day-btn"
      title="End my day — punch out, close breaks & log out"
    >
      <LogOut className="h-4 w-4" />
      <span>{busy ? 'Ending…' : 'End my day'}</span>
    </Button>
  );
};
